import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import { fetchMortgageNotes } from "../redux/slices/mortgageNotesSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { FetchNotesParams } from "../redux/interfaces/mortgageNote.redux.interface";
import { NoteStatus } from "../interfaces/mortgageNote";
import NoteCardGrid from "../components/notes/NoteCardGrid";
import FilterToolbar from "../components/notes/FilterToolbar";
import NotesHeader from "../components/notes/NotesHeader";
import AdminToolbar from "../components/admin/AdminToolbar";
import useAuth from "../hooks/useAuth";

const Notes = () => {
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const location = useLocation();

  const notesRef = useRef<HTMLDivElement | null>(null);

  const { currentAdmin } = useAuth();

  const { notes, status, hasMore } = useSelector(
    (state: RootState) => state.mortgageNotes
  );

  const [filters, setFilters] = useState<FetchNotesParams>({
    addressState: [],
    targetMin: undefined,
    targetMax: undefined,
    loanMin: undefined,
    loanMax: undefined,
    status: [],
    offset: 0,
    limit: 10,
  });

  // const [searchQuery, setSearchQuery] = useState("");

  const loadMoreNotes = useCallback(() => {
    if (status === "idle" && hasMore) {
      dispatch(fetchMortgageNotes({ ...filters, offset: notes.length }));
    }
  }, [dispatch, filters, hasMore, notes.length, status]);

  useEffect(() => {
    const handleScroll = () => {
      if (notesRef.current) {
        const { bottom } = notesRef.current.getBoundingClientRect();
        if (bottom <= window.innerHeight) {
          loadMoreNotes();
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMoreNotes]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    const addressState = queryParams.get("addressState")?.split(",") || [];
    const targetMin = Number(queryParams.get("targetMin")) || undefined;
    const targetMax = Number(queryParams.get("targetMax")) || undefined;
    const loanMin = Number(queryParams.get("loanMin")) || undefined;
    const loanMax = Number(queryParams.get("loanMax")) || undefined;
    const status = queryParams.get("status")?.split(",") || [];
    const search = queryParams.get("search") || "";

    const newFilters = {
      addressState,
      targetMin,
      targetMax,
      loanMin,
      loanMax,
      status: status as NoteStatus[],
      offset: 0,
      limit: 10,
      searchQuery: search,
    };

    setFilters(newFilters);
    // setSearchQuery(search);
    dispatch(fetchMortgageNotes(newFilters));
  }, [dispatch, location.search]);

  const updateFilters = (newFilters: Partial<FetchNotesParams>) => {
    const updatedFilters = { ...filters, ...newFilters, offset: 0 };
    setFilters(updatedFilters);

    const queryParams = new URLSearchParams();

    if (updatedFilters.addressState?.length)
      queryParams.set("addressState", updatedFilters.addressState.join(","));
    if (updatedFilters.targetMin !== undefined)
      queryParams.set("targetMin", updatedFilters.targetMin.toString());
    if (updatedFilters.targetMax !== undefined)
      queryParams.set("targetMax", updatedFilters.targetMax.toString());
    if (updatedFilters.loanMin !== undefined)
      queryParams.set("loanMin", updatedFilters.loanMin.toString());
    if (updatedFilters.loanMax !== undefined)
      queryParams.set("loanMax", updatedFilters.loanMax.toString());
    if (updatedFilters.status?.length)
      queryParams.set("status", updatedFilters.status.join(","));

    if (updatedFilters.searchQuery)
      queryParams.set("search", updatedFilters.searchQuery);

    navigate({ search: queryParams.toString() });

    dispatch(fetchMortgageNotes(updatedFilters));
  };

  const handleSearch = (query: string) => {
    const updatedFilters = {
      ...filters,
      searchQuery: query,
      addressState: [],
      targetMin: undefined,
      targetMax: undefined,
      loanMin: undefined,
      loanMax: undefined,
      status: [],
      offset: 0,
    };
    setFilters(updatedFilters);

    const queryParams = new URLSearchParams();
    if (query) queryParams.set("search", query);

    navigate({ search: queryParams.toString() });

    dispatch(fetchMortgageNotes(updatedFilters));
  };

  return (
    <>
      <div className="w-full flex flex-col items-center justify-center">
        <div className="w-full px-2 md:px-8 lg:px-12 xl:px-24 max-w-[1320px]">
          <NotesHeader />
          <FilterToolbar onFilterChange={updateFilters} />
          <div className="mb-4 ">
            <div className="text-main-gray font-medium w-full">
              <span className="font-extrabold">AVAILABLE MORTGAGE NOTES: </span>
              Through our platform, you get direct access to exclusive real
              estate lending opportunities that you would never see otherwise.
              We provide wide and diverse property types, loan amounts, and loan
              terms so that you can build your own portfolio your{" "}
              <span>OWN WAY</span>, while benefiting from the diversification of
              a broad spectrum of lending opportunities.
              <div className="my-3"></div>
              Browse these exclusively available and pre-evaluated notes that
              have gone through superior due diligence processes and fraud
              detection systems.
            </div>
          </div>

          {currentAdmin && (
            <div className="py-2 pb-4  w-full">
              <AdminToolbar onSearch={handleSearch} />
            </div>
          )}
          <div className="w-full flex justify-center " ref={notesRef}>
            <NoteCardGrid notes={notes} status={status} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Notes;
